import { useState } from 'react'
import { SOURCE_SYSTEMS } from './constants'

const StepOdooConnection = ({
  formData,
  selectedOrganization,
  onGoToStep,
  // Odoo connection state
  odooUrl,
  odooUser,
  odooPassword,
  odooDb,
  showDatabaseField,
  availableDatabases,
  onOdooFieldChange,
  // Verification state
  verifying,
  verificationResult,
  companies,
  selectedCompanyId,
  odooVersion,
  onVerifyConnection,
  onCompanySelect
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false)

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#111418] flex items-center gap-2">
        <span className="material-symbols-outlined text-primary">link</span>
        Connect to Odoo
      </h2>

      {/* Summary of previous steps */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-gray-400 text-sm">domain</span>
            <span className="text-sm text-gray-600">{selectedOrganization?.name || formData.new_client_name}</span>
          </div>
          <button type="button" onClick={() => onGoToStep(1)} className="text-xs text-primary hover:underline">Change</button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-gray-400 text-sm">description</span>
            <span className="text-sm text-gray-600">{SOURCE_SYSTEMS.find(s => s.id === formData.source_system)?.name} → Odoo</span>
          </div>
          <button type="button" onClick={() => onGoToStep(2)} className="text-xs text-primary hover:underline">Change</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Odoo URL */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Odoo URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={odooUrl}
            onChange={(e) => onOdooFieldChange('url', e.target.value)}
            placeholder="https://yourcompany.odoo.com"
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          />
          <p className="text-xs text-gray-400">The URL of your Odoo instance</p>
        </div>

        {/* Database Name - Only shown when multiple databases or auto-detection fails */}
        {showDatabaseField && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Database <span className="text-red-500">*</span>
            </label>
            {availableDatabases.length > 0 ? (
              <select
                value={odooDb}
                onChange={(e) => onOdooFieldChange('db', e.target.value)}
                className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value="">Select a database...</option>
                {availableDatabases.map(db => (
                  <option key={db} value={db}>{db}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={odooDb}
                onChange={(e) => onOdooFieldChange('db', e.target.value)}
                placeholder="yourcompany"
                className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
            )}
            <p className="text-xs text-gray-400">
              {availableDatabases.length > 0
                ? 'Multiple databases found. Please select one.'
                : 'Could not auto-detect the database. Please enter it manually.'}
            </p>
          </div>
        )}

        {/* Username */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Username <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={odooUser}
            onChange={(e) => onOdooFieldChange('user', e.target.value)}
            placeholder="user@yourcompany.com"
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          />
          <p className="text-xs text-gray-400">Your Odoo login email</p>
        </div>

        {/* Password / API Key */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            API Key / Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={odooPassword}
              onChange={(e) => onOdooFieldChange('password', e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 pr-10 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <span className="material-symbols-outlined text-lg">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={() => setShowApiKeyHelp(!showApiKeyHelp)}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-sm">help</span>
              How to create an API key
              <span className="material-symbols-outlined text-sm">
                {showApiKeyHelp ? 'expand_less' : 'expand_more'}
              </span>
            </button>
          </div>
          {showApiKeyHelp && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-gray-700">
              <p className="font-medium text-blue-800 mb-2">Creating an Odoo API Key:</p>
              <ol className="list-decimal list-inside space-y-1.5 text-gray-600">
                <li>Log in to your Odoo instance</li>
                <li>Click your profile icon in the top-right corner</li>
                <li>Select <strong>My Profile</strong> or <strong>Preferences</strong></li>
                <li>Go to the <strong>Account Security</strong> tab</li>
                <li>Click <strong>New API Key</strong></li>
                <li>Enter a description (e.g., &quot;Migration Tool&quot;) and confirm with your password</li>
                <li>Copy the generated key - it will only be shown once</li>
              </ol>
              <p className="mt-2 text-blue-700">
                <span className="material-symbols-outlined text-sm align-text-bottom mr-1">lightbulb</span>
                API keys are more secure than passwords and can be revoked at any time.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Verify Connection Button */}
      <div className="flex items-center gap-4 pt-2">
        <button
          type="button"
          onClick={onVerifyConnection}
          disabled={verifying || !odooUrl || !odooUser || !odooPassword || (showDatabaseField && !odooDb)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-bold text-sm rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {verifying ? (
            <>
              <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              Verifying...
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-sm">verified</span>
              Verify Connection
            </>
          )}
        </button>

        {/* Verification Result */}
        {verificationResult && (
          <div className={`flex items-center gap-2 ${verificationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
            <span className="material-symbols-outlined text-sm">
              {verificationResult.valid ? 'check_circle' : 'error'}
            </span>
            <span className="text-sm font-medium">
              {verificationResult.valid
                ? `Connected to Odoo ${odooVersion || ''}`
                : verificationResult.error_message || 'Connection failed'}
            </span>
          </div>
        )}
      </div>

      {/* Company Selection Dropdown - Only show after successful verification */}
      {verificationResult?.valid && companies.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Company <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedCompanyId || ''}
            onChange={(e) => onCompanySelect(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none max-w-md"
          >
            <option value="">Select a company...</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400">
            Select the Odoo company for this migration
          </p>
        </div>
      )}

      {/* Security Notice */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-100">
        <span className="material-symbols-outlined text-amber-600 mt-0.5">lock</span>
        <div>
          <p className="text-sm font-bold text-amber-800">Secure Credential Storage</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Your credentials are encrypted using PGP before being stored. They cannot be retrieved or viewed after submission.
          </p>
        </div>
      </div>
    </div>
  )
}

export default StepOdooConnection
